import { Trans, useLingui } from "@lingui/react/macro";
import { isEqual } from "lodash";
import { observable, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";

import type { Column } from "./data-table/types";

import { Button } from "../button";
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "../dialog";
import { Checkbox } from "../fields/checkbox";
import { ComboboxMultiple } from "../fields/combobox-multiple";
import { DateInput } from "../fields/date-input";
import { NumberInput } from "../fields/number-input";
import { Select } from "../fields/select";
import { TagsInput } from "../fields/tags-input";
import { TextInput } from "../fields/text-input";
import { type EnumValues, valuesToMap } from "../fields/utils";
import { Form } from "../form/form";
import { FormRow } from "../form/form-row";
import { FormTable } from "../form/form-table";
import { LazyRichTextEditor } from "../lazy-rich-text-editor";

export function EditRowDialog<T extends object>({
  columns,
  item,
  onSave,
  onClose,
  patchAlwaysIncludeKeys,
}: {
  columns: Column<T>[];
  item: T;
  title?: string;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
  patchAlwaysIncludeKeys?: string[];
}) {
  const { t } = useLingui();
  const editableColumns = useMemo(
    () =>
      columns.filter((column) => {
        const canEdit =
          typeof column.editable === "function"
            ? column.editable(item)
            : !!column.editable;
        return canEdit;
      }),
    [columns, item],
  );

  const [fields] = useState<Record<string, unknown>>(() =>
    observable(initFields(editableColumns, item)),
  );

  const formColumns = useMemo(
    () =>
      editableColumns.filter(
        (column) =>
          column.type !== undefined ||
          (column.editInDialog && column.cell !== undefined),
      ),
    [editableColumns],
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      className="max-h-[90dvh]"
    >
      <DialogHeader>
        <Trans>Edit row</Trans>
      </DialogHeader>

      <Form
        className="flex flex-col min-h-0 flex-1"
        onSubmit={async () => {
          const patch: Record<string, unknown> = {};
          const itemRecord = item as Record<string, unknown>;

          for (const key of Object.keys(fields)) {
            // Deep compare, and drop the observable wrapper first: array-valued
            // fields (a medication's dosage) are new observable arrays here, so
            // identity would report them changed on every save.
            const value = toJS(fields[key]);
            if (!isEqual(value, itemRecord[key])) {
              patch[key] = value;
            }
          }

          if (Object.keys(patch).length === 0) {
            onClose();
            return;
          }

          for (const key of patchAlwaysIncludeKeys ?? []) {
            if (Object.prototype.hasOwnProperty.call(fields, key)) {
              patch[key] = toJS(fields[key]);
            } else {
              patch[key] = itemRecord[key];
            }
          }

          await onSave(patch);
          onClose();
        }}
      >
        <DialogBody>
          <FormTable dataAdd>
            {formColumns.map((column) => {
              const attr = toAttrKey(column.attr);
              // Headers are usually translatable nodes (`<Trans>`); render them
              // directly so the edit dialog stays localized. Fall back to the
              // attribute key only for cell-only columns with no header.
              const label = column.label ?? column.header ?? attr ?? t`Field`;

              return (
                <FormRow
                  key={attr ?? String(column.header)}
                  label={label}
                  data={renderField(column, fields, item)}
                />
              );
            })}
          </FormTable>
        </DialogBody>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" type="submit">
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
}

/**
 * A custom-cell column rendered inside the dialog form. Merges the draft into
 * `item` the way the table's EditableCell does, so a cell that derives state
 * from a sibling field — e.g. the option list for an event's category details,
 * which depends on the chosen category type — follows the draft rather than the
 * saved row. `observer` keeps that merge live without re-rendering the whole
 * form on every keystroke.
 */
const DraftCell = observer(function DraftCell<T extends object>({
  column,
  item,
  fields,
}: {
  column: Column<T>;
  item: T;
  fields: Record<string, unknown>;
}) {
  return (
    <>
      {column.cell?.({
        item: { ...item, ...fields },
        patch: fields,
        attr: column.attr,
        index: () => 0,
      })}
    </>
  );
});

function renderField<T extends object>(
  column: Column<T>,
  fields: Record<string, unknown>,
  item: T,
) {
  if (column.editInDialog && column.cell) {
    return <DraftCell column={column} item={item} fields={fields} />;
  }

  const attr = toAttrKey(column.attr);
  if (!attr) {
    return null;
  }

  if (column.type === "string") {
    return (
      <TextInput
        model={fields}
        attr={attr}
        multiline={column.multiline ? "autosize" : undefined}
      />
    );
  }

  if (column.type === "richtext") {
    return (
      <LazyRichTextEditor
        className="w-[330px]"
        toolbar={["text-formatting"]}
        value={String(fields[attr] ?? "")}
        onChange={(v) => {
          fields[attr] = v;
        }}
      />
    );
  }

  if (column.type === "number") {
    return <NumberInput model={fields} attr={attr} />;
  }

  if (column.type === "boolean") {
    return <Checkbox model={fields} attr={attr} />;
  }

  if (column.type === "enum") {
    return <Select model={fields} attr={attr} values={column.values} />;
  }

  if (column.type === "array") {
    if (column.values) {
      return (
        <ComboboxMultiple
          model={fields}
          attr={attr}
          options={valuesToOptions(column.values)}
        />
      );
    }

    return <TagsInput model={fields} attr={attr} />;
  }

  if (column.type === "datetime" || column.type === "date") {
    return (
      <DateInput
        model={fields}
        attr={attr}
        type={column.type === "datetime" ? "datetime-local" : "date"}
      />
    );
  }

  return null;
}

export function initFields<T extends object>(columns: Column<T>[], item: T) {
  const fields: Record<string, unknown> = {};

  for (const column of columns) {
    const attr = toAttrKey(column.attr);
    if (!attr) {
      continue;
    }
    // Deep copy: rows come from a DataModel's observable `data`, so an
    // array-valued field (a medication's dosage) is already an observable array
    // — and wrapping the draft in `observable()` keeps that same array rather
    // than copying it. The draft would then edit the row in place, leaving the
    // submit-time diff with nothing to send.
    fields[attr] = toJS((item as Record<string, unknown>)[attr]);
  }

  return fields;
}

function toAttrKey(attr: PropertyKey | undefined): string | undefined {
  return typeof attr === "string" ? attr : undefined;
}

function valuesToOptions(values: EnumValues) {
  return [...valuesToMap(values).entries()].map(([value, label]) => ({
    value,
    label,
  }));
}
